import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Mail, CheckCircle, ShieldBan, UserCheck } from 'lucide-react'
import type { TriageEmailData } from '../../../shared/types/chat'
import { useChatActions } from '../ChatContext'
import { formatDisplayTime } from '@tendbot/ui'
import TriageClaimConfirmModal from './TriageClaimConfirmModal'

interface TriageEmailCardProps {
  email: TriageEmailData
  messageId: string
}

export default function TriageEmailCard({ email, messageId }: TriageEmailCardProps) {
  const { t } = useTranslation('chat')
  const { onTriageAction } = useChatActions()
  const [claimConfirmOpen, setClaimConfirmOpen] = useState(false)

  const resolved = email.resolvedAction ?? (email.analyzed ? 'accepted' : null)

  return (
    <div className="rounded-2xl border-l-4 border-l-gold/60 bg-charcoal shadow-[0_1px_0_rgba(255,255,255,0.03)_inset,0_8px_24px_rgba(0,0,0,0.30)] px-3 py-3 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-gold/70 flex-shrink-0" />
        <span className="text-sm text-ivory/80 font-medium truncate">{email.from}</span>
        <span className="ml-auto text-[10px] text-pearl/60 flex-shrink-0">
          {formatDisplayTime(email.receivedAt)}
        </span>
      </div>

      {/* Subject */}
      <p className="text-sm text-ivory/90 font-medium truncate pl-[22px]">
        {email.subject}
      </p>

      {/* Snippet */}
      <p className="text-xs text-pearl/60 line-clamp-3 pl-[22px]">
        {email.snippet}
      </p>

      {/* Triage reason */}
      {email.triageReason && (
        <p className="text-[10px] text-pearl/60 pl-[22px]">
          {t('triage.reviewReason')}: {email.triageReason}
        </p>
      )}

      {/* Actions */}
      <div className="pl-[22px]">
        {resolved ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-1.5"
          >
            {resolved === 'blocked' ? (
              <>
                <ShieldBan size={12} className="text-rose/70" />
                <span className="text-xs text-rose/70">{t('triage.blocked')}</span>
              </>
            ) : resolved === 'claimed' ? (
              <>
                <UserCheck size={12} className="text-gold/70" />
                <span className="text-xs text-gold/70">{t('triage.claimed')}</span>
              </>
            ) : (
              <>
                <CheckCircle size={12} className="text-emerald/70" />
                <span className="text-xs text-emerald/70">{t('triage.accepted')}</span>
              </>
            )}
          </motion.div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onTriageAction(messageId, email.signalId, 'accepted')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald/30 text-emerald/80 text-xs hover:bg-emerald/10 transition-colors"
            >
              <CheckCircle size={12} />
              {t('triage.accept')}
            </button>
            <button
              onClick={() => onTriageAction(messageId, email.signalId, 'blocked')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-rose/30 text-rose/80 text-xs hover:bg-rose/10 transition-colors"
            >
              <ShieldBan size={12} />
              {t('triage.block')}
            </button>
            <button
              onClick={() => setClaimConfirmOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-gold/30 text-gold/80 text-xs hover:bg-gold/10 transition-colors"
            >
              <UserCheck size={12} />
              {t('triage.myEmail')}
            </button>
          </div>
        )}
      </div>

      <TriageClaimConfirmModal
        open={claimConfirmOpen}
        fromEmail={email.from}
        onClose={() => setClaimConfirmOpen(false)}
        onConfirm={() => {
          setClaimConfirmOpen(false)
          onTriageAction(messageId, email.signalId, 'claimed')
        }}
      />
    </div>
  )
}
